import { useMutation } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import AuthHeadline from '../Components/AuthHeadline';
import AuthLayout from '../Components/AuthLayout';
import RegistrationForm from '../Components/Forms/RegistrationForm';
import ResendVerification from '../Components/ResendVerification';
import { useLogEvent, useLogEventOnMount } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import useAutoLogout from '../hooks/useAutoLogout';
import React, { FC } from 'react';
import { IRegisterValues } from 'FeatureModules/Auth/types/auth.types';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import omit from 'lodash/omit';
import { REGISTER_USER } from 'graphql/secure/mutations';
import { RESEND_USER_VERIFICATION_MAIL } from 'graphql/auth/mutations/auth.mutations';

export const Subtitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const Register: FC = () => {
  useLogEventOnMount('VIEW_REGISTER');
  const { logEvent } = useLogEvent();

  const [signedUp, setSignedUp] = useQueryParam('registered', BooleanParam);
  const [email, setEmail] = useQueryParam('email', StringParam);

  // Logout user when still found
  useAutoLogout();

  const { setErrorMessage } = useMessages();

  const [register] = useMutation<{ input: IRegisterValues }>(REGISTER_USER, {
    onError: error => setErrorMessage(error.message),
    onCompleted: () => {
      setSignedUp(true);
      logEvent('USER_REGISTERED', {
        email,
      });
    },
    fetchPolicy: 'no-cache',
  });

  const onSubmit = (values: IRegisterValues) => {
    setEmail(values.email);

    return register({ variables: { input: omit(values, 'confirmPassword') } });
  };

  return (
    <AuthLayout title="Register">
      <AuthHeadline text="Welcome to" highlighted="the transparency tool" />
      {signedUp ? (
        <ResendVerification
          title="Verify your email address"
          subtitle={`We have sent a verification link to ${email}. It might take a
            moment for the mail to arrive. Please check your inbox and spam
            folder.`}
          email={email || ''}
          mutation={RESEND_USER_VERIFICATION_MAIL}
        />
      ) : (
        <Box mt={5}>
          <RegistrationForm onSubmit={onSubmit} />
        </Box>
      )}
    </AuthLayout>
  );
};

export default Register;
